import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type Theme = 'classic' | 'sunset' | 'cosmic'

// Type for old theme values (before migration)
type OldTheme = 'luxury' | 'ocean' | 'aurora' | 'noir' | 'ethereal'

// Migration map for old theme names to new ones
const THEME_MIGRATION_MAP: Record<OldTheme, Theme> = {
  luxury: 'classic',
  ocean: 'cosmic', // ocean -> cosmic (cool palette)
  aurora: 'sunset', // aurora -> sunset (warm palette)
  noir: 'cosmic', // noir -> cosmic (cool palette)
  ethereal: 'cosmic', // ethereal -> cosmic (cool palette)
}

// Available music tracks - add your file names here
export const MUSIC_TRACKS = [
  '/assets/audio/music/track-01.mp3',
  '/assets/audio/music/track-02.mp3',
  '/assets/audio/music/track-03.mp3',
  '/assets/audio/music/track-04.mp3',
] as const

export type MusicTrackIndex = 0 | 1 | 2 | 3

interface AudioSettings {
  musicEnabled: boolean
  musicVolume: number
  currentTrackIndex: MusicTrackIndex
  sfxEnabled: boolean
  sfxVolume: number
}

interface BackgroundSettings {
  animatedCardsEnabled: boolean
}

interface SettingsState {
  // Theme
  theme: Theme
  setTheme: (theme: Theme) => void

  // Audio
  audio: AudioSettings
  setMusicEnabled: (enabled: boolean) => void
  setMusicVolume: (volume: number) => void
  setSfxEnabled: (enabled: boolean) => void
  setSfxVolume: (volume: number) => void
  nextTrack: () => void
  previousTrack: () => void
  setTrack: (index: MusicTrackIndex) => void

  // Background
  background: BackgroundSettings
  setAnimatedCardsEnabled: (enabled: boolean) => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      // Theme defaults
      theme: 'classic',
      setTheme: (theme) => set({ theme }),

      // Audio defaults
      audio: {
        musicEnabled: false,
        musicVolume: 0.5,
        currentTrackIndex: 0,
        sfxEnabled: false,
        sfxVolume: 0.7,
      },
      setMusicEnabled: (enabled) =>
        set((state) => ({ audio: { ...state.audio, musicEnabled: enabled } })),
      setMusicVolume: (volume) =>
        set((state) => ({ audio: { ...state.audio, musicVolume: Math.max(0, Math.min(1, volume)) } })),
      setSfxEnabled: (enabled) =>
        set((state) => ({ audio: { ...state.audio, sfxEnabled: enabled } })),
      setSfxVolume: (volume) =>
        set((state) => ({ audio: { ...state.audio, sfxVolume: Math.max(0, Math.min(1, volume)) } })),
      nextTrack: () =>
        set((state) => {
          const nextIndex = (state.audio.currentTrackIndex + 1) % MUSIC_TRACKS.length
          return { audio: { ...state.audio, currentTrackIndex: nextIndex as MusicTrackIndex } }
        }),
      previousTrack: () =>
        set((state) => {
          const prevIndex = state.audio.currentTrackIndex === 0
            ? MUSIC_TRACKS.length - 1
            : state.audio.currentTrackIndex - 1
          return { audio: { ...state.audio, currentTrackIndex: prevIndex as MusicTrackIndex } }
        }),
      setTrack: (index) =>
        set((state) => ({ audio: { ...state.audio, currentTrackIndex: index } })),

      // Background defaults
      background: {
        animatedCardsEnabled: false,
      },
      setAnimatedCardsEnabled: (enabled) =>
        set((state) => ({ background: { ...state.background, animatedCardsEnabled: enabled } })),
    }),
    {
      name: 'modern-art-settings',
      migrate: (persistedState: any, version: number) => {
        // Migrate old theme names to new theme names
        if (persistedState?.theme && THEME_MIGRATION_MAP[persistedState.theme as OldTheme]) {
          persistedState.theme = THEME_MIGRATION_MAP[persistedState.theme as OldTheme]
        }
        return persistedState as SettingsState
      },
      version: 1,
    }
  )
)
